"use client";

import { VUES } from "@/lib/data";
import { useStock } from "@/context/StockContext";
import { Icon } from "@/components/Icons";
import { Panel } from "@/components/Panel";
import { Toast } from "@/components/Toast";
import { AccueilView } from "@/components/views/AccueilView";
import { StockView } from "@/components/views/StockView";
import { ReceptionView } from "@/components/views/ReceptionView";
import { VentesView } from "@/components/views/VentesView";
import { FournisseursView } from "@/components/views/FournisseursView";
import { FournisseursConfigView } from "@/components/views/FournisseursConfigView";
import { InventaireView } from "@/components/views/InventaireView";
import { AssociationsView } from "@/components/views/AssociationsView";
import { FusionsView } from "@/components/views/FusionsView";
import type { VueId } from "@/lib/types";

function View() {
  const { vue } = useStock();
  switch (vue) {
    case "accueil":
      return <AccueilView />;
    case "stock":
      return <StockView />;
    case "reception":
      return <ReceptionView />;
    case "ventes":
      return <VentesView />;
    case "fournisseurs":
      return <FournisseursView />;
    case "fournisseursConfig":
      return <FournisseursConfigView />;
    case "inventaire":
      return <InventaireView />;
    case "associations":
      return <AssociationsView />;
    case "fusions":
      return <FusionsView />;
  }
}

export function StockApp() {
  const { vue, go, badge, syncMin, syncing, sync } = useStock();
  const current = VUES.find((v) => v.id === vue)!;

  const navBtn = (v: (typeof VUES)[number], court = false) => {
    const b = badge(v.id as VueId);
    return (
      <button
        key={v.id}
        type="button"
        aria-current={vue === v.id ? "page" : undefined}
        onClick={() => go(v.id)}
      >
        <Icon name={v.id} />
        <span>{court ? v.court : v.lib}</span>
        {b ? <span className="count">{b}</span> : null}
      </button>
    );
  };

  return (
    <>
      <div className="app">
        <aside className="side" aria-label="Navigation">
          <div className="brand">
            <div className="brand-mark">S</div>
            <div>
              <b>STOCK</b>
              <small>Entrepôt · 800 m²</small>
            </div>
          </div>
          <nav className="nav">{VUES.map((v) => navBtn(v))}</nav>
          <div className="side-foot">
            <span className="eyebrow">Connexions</span>
            <span>
              <span className="dot" style={{ display: "inline-block", marginRight: 8 }} />
              Pennylane
            </span>
            <span className="muted">Ventes PrestaShop + magasin</span>
          </div>
        </aside>

        <main>
          <header className="top">
            <div>
              <p className="eyebrow">{current.eyebrow}</p>
              <h1>{current.lib}</h1>
            </div>
            <div className="top-r">
              <span className="sync">
                <span className="dot" />
                <span>
                  {syncing
                    ? "Lecture de Pennylane…"
                    : syncMin < 0
                      ? "Pennylane · pas encore lu"
                      : syncMin === 0
                        ? "Pennylane · synchronisé à l'instant"
                        : `Pennylane · synchronisé il y a ${syncMin} min`}
                </span>
              </span>
              <button
                className="btn small"
                type="button"
                disabled={syncing}
                onClick={() => void sync(true)}
              >
                <Icon name="sync" />
                Synchroniser
              </button>
            </div>
          </header>
          <p className="demo">Maquette · données d&apos;exemple, rien n&apos;est enregistré</p>
          <section aria-live="polite">
            <View />
          </section>
        </main>
      </div>

      <nav className="tabs" aria-label="Navigation">
        {VUES.filter(
          (v) =>
            v.id !== "fournisseurs" &&
            v.id !== "fournisseursConfig" &&
            v.id !== "associations" &&
            v.id !== "fusions",
        ).map((v) => navBtn(v, true))}
      </nav>

      <Panel />
      <Toast />
    </>
  );
}
